import { ScreenSpaceEventHandler, defined, ScreenSpaceEventType, JulianDate, Color, ConstantProperty, PropertyBag, Cartographic, Ellipsoid, Rectangle, CallbackProperty } from "cesium";
import { getUsernameAndUserIdFormat, removeEpochSuffix, removeFileExtension, showToast } from "../Utility";
import Type from "../Types";
import { disableCameraControls, restoreCameraControls } from "../MapUtility";

export const createAutoAssociationLayerClickHandler = (viewer, _ignored, onAssociation, mapObjectsRef, setMapObjects, userData) => {
  // --- Time ---
  const timeNow = () => JulianDate.now();

  // --- Normalizer ---
  const normalizeKey = (raw) => {
    const noExt = removeFileExtension?.(String(raw || "")) ?? String(raw || "");
    const noEpoch = removeEpochSuffix?.(noExt) ?? noExt;
    return String(noEpoch)
      .toLowerCase()
      .replace(/[\s\-_]/g, "");
  };

  const getNormalizedLayerKey = (entity) => {
    const owner = entity?.entityCollection?.owner;
    let nm = owner?.layerName ?? owner?.name ?? null;
    if (!nm && viewer?.dataSources) {
      const dsc = viewer.dataSources;
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        if (ds?.entities?.contains?.(entity)) {
          nm = ds.layerName ?? ds.name ?? null;
          if (nm) break;
        }
      }
    }
    if (!nm) return "";
    return normalizeKey(nm);
  };

  const cartographicInsideRectangle = (rect, carto) => {
    if (!rect || !carto) return false;
    const lon = carto.longitude;
    const lat = carto.latitude;
    return lon >= rect.west && lon <= rect.east && lat >= rect.south && lat <= rect.north;
  };

  // helper for orientation
  const orientation = (ax, ay, bx, by, cx, cy) => {
    const v = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    if (v > 0) return 1; // ccw
    if (v < 0) return -1; // cw
    return 0; // colinear
  };

  const onSegment = (ax, ay, bx, by, cx, cy) => {
    return Math.min(ax, bx) <= cx && cx <= Math.max(ax, bx) && Math.min(ay, by) <= cy && cy <= Math.max(ay, by);
  };

  const segmentsIntersect2D = (p1, p2, p3, p4) => {
    const o1 = orientation(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y);
    const o2 = orientation(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y);
    const o3 = orientation(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y);
    const o4 = orientation(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y);

    if (o1 !== o2 && o3 !== o4) return true;

    if (o1 === 0 && onSegment(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y)) return true;
    if (o2 === 0 && onSegment(p1.x, p1.y, p2.x, p2.y, p4.x, p4.y)) return true;
    if (o3 === 0 && onSegment(p3.x, p3.y, p4.x, p4.y, p1.x, p1.y)) return true;
    if (o4 === 0 && onSegment(p3.x, p3.y, p4.x, p4.y, p2.x, p2.y)) return true;

    return false;
  };

  const segmentIntersectsRectangleCartographic = (rect, c1, c2) => {
    if (!rect || !c1 || !c2) return false;

    const p1 = { x: c1.longitude, y: c1.latitude };
    const p2 = { x: c2.longitude, y: c2.latitude };

    // quick check: one endpoint inside
    if (cartographicInsideRectangle(rect, c1) || cartographicInsideRectangle(rect, c2)) {
      return true;
    }

    const left = rect.west;
    const right = rect.east;
    const bottom = rect.south;
    const top = rect.north;

    const r1 = { x: left, y: bottom };
    const r2 = { x: right, y: bottom };
    const r3 = { x: right, y: top };
    const r4 = { x: left, y: top };

    if (segmentsIntersect2D(p1, p2, r1, r2)) return true;
    if (segmentsIntersect2D(p1, p2, r2, r3)) return true;
    if (segmentsIntersect2D(p1, p2, r3, r4)) return true;
    if (segmentsIntersect2D(p1, p2, r4, r1)) return true;

    return false;
  };

  // --- Safe props ---
  const getPropValue = (entity, propName) => {
    const pbag = entity?.properties;
    if (!pbag) return null;
    const p = pbag[propName];
    if (!p) return null;
    try {
      return typeof p.getValue === "function" ? p.getValue(timeNow()) : p;
    } catch {
      return null;
    }
  };

  const upsertCesiumPropertyString = (entity, key, valueToAdd) => {
    if (!entity.properties) entity.properties = new PropertyBag({});
    if (!(key in entity.properties) || typeof entity.properties[key]?.getValue !== "function") {
      entity.properties.addProperty?.(key);
    }
    const current = getPropValue(entity, key);
    const parts = current
      ? String(current)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    const val = String(valueToAdd);
    if (!parts.includes(val)) parts.push(val);
    entity.properties[key] = new ConstantProperty(parts.join(","));

    if (attEditor) {
      if (!("Att_editor" in entity.properties) || typeof entity.properties.Att_editor?.getValue !== "function") {
        entity.properties.addProperty?.("Att_editor");
      }
      entity.properties.Att_editor = new ConstantProperty(attEditor);
    }
  };

  const attEditor = getUsernameAndUserIdFormat(userData);

  const removeCesiumPropertyString = (entity, key, valueToRemove) => {
    const current = getPropValue(entity, key);
    if (!current) return;
    const parts = String(current)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((v) => v !== String(valueToRemove));
    if (!entity.properties) entity.properties = new PropertyBag({});
    if (!(key in entity.properties)) {
      entity.properties.addProperty?.(key);
    }
    entity.properties[key] = new ConstantProperty(parts.join(","));
  };

  const getAllEntities = () => {
    const result = [];
    const vEnts = viewer?.entities;
    if (vEnts?.values && Array.isArray(vEnts.values)) {
      for (const e of vEnts.values) {
        if (!e) continue;
        result.push(e);
      }
    }
    const dsc = viewer?.dataSources;
    if (dsc) {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ents = ds?.entities?.values;
        if (Array.isArray(ents)) {
          for (const e of ents) {
            if (!e) continue;
            result.push(e);
          }
        }
      }
    }
    return result;
  };

  // --- Style backup/restore ---
  const originalStyles = new Map();
  const highlightEntity = (entity, color) => {
    if (!entity) return;
    if (!originalStyles.has(entity.id)) {
      originalStyles.set(entity.id, {
        polygonMaterial: entity.polygon?.material,
        polylineMaterial: entity.polyline?.material,
        pointColor: entity.point?.color,
      });
    }
    if (entity.polygon) entity.polygon.material = color;
    if (entity.polyline) entity.polyline.material = color;
    if (entity.point) entity.point.color = color;
  };
  const findEntityById = (id) => {
    const fromViewer = viewer?.entities?.getById?.(id);
    if (fromViewer) return fromViewer;
    const dsc = viewer?.dataSources;
    if (dsc) {
      for (let i = 0; i < dsc.length; i++) {
        const ds = dsc.get(i);
        const ent = ds?.entities?.getById?.(id);
        if (ent) return ent;
      }
    }
    return null;
  };
  const restoreEntity = (entity) => {
    const b = originalStyles.get(entity?.id);
    if (!b) return;
    if (entity.polygon && Object.prototype.hasOwnProperty.call(b, "polygonMaterial")) entity.polygon.material = b.polygonMaterial;
    if (entity.polyline && Object.prototype.hasOwnProperty.call(b, "polylineMaterial")) entity.polyline.material = b.polylineMaterial;
    if (entity.point && Object.prototype.hasOwnProperty.call(b, "pointColor")) entity.point.color = b.pointColor;
  };
  const restoreAll = () => {
    for (const [id] of originalStyles.entries()) restoreEntity(findEntityById(id));
    originalStyles.clear();
  };

  const lockedHighlightIds = new Set();
  const boxSelectedIds = new Set();

  const markEdited = (entity) => {
    try {
      const temp = [...(mapObjectsRef?.current || [])];
      for (const obj of temp) {
        if (obj?.dataSource?.entities?.contains?.(entity)) {
          obj.is_edited = true;
          break;
        }
      }
      setMapObjects?.(temp);
    } catch (e) {
      console.warn?.("[createAssociationFastLayerClickHandler] Failed to flag is_edited:", e);
    }
  };

  // --- Rectangle helpers ---
  const windowPosToCartesian = (winPos) => {
    if (!winPos) return null;
    const ray = viewer.camera.getPickRay(winPos);
    const globe = viewer.scene.globe;
    return globe?.pick?.(ray, viewer.scene) || viewer.camera.pickEllipsoid(winPos, Ellipsoid.WGS84);
  };

  const currentRect = new Rectangle();
  const startCarto = new Cartographic();
  const endCarto = new Cartographic();

  const updateCurrentRectFromWindow = (p1, p2) => {
    const c1 = windowPosToCartesian(p1);
    const c2 = windowPosToCartesian(p2);
    if (!c1 || !c2) return false;
    const carto1 = Ellipsoid.WGS84.cartesianToCartographic(c1, startCarto);
    const carto2 = Ellipsoid.WGS84.cartesianToCartographic(c2, endCarto);
    const west = Math.min(carto1.longitude, carto2.longitude);
    const east = Math.max(carto1.longitude, carto2.longitude);
    const south = Math.min(carto1.latitude, carto2.latitude);
    const north = Math.max(carto1.latitude, carto2.latitude);
    currentRect.west = west;
    currentRect.east = east;
    currentRect.south = south;
    currentRect.north = north;
    return true;
  };

  const cartesianInsideRectangle = (rect, cart) => {
    if (!rect || !cart) return false;
    const carto = Ellipsoid.WGS84.cartesianToCartographic(cart);
    if (!carto) return false;
    const west = rect.west;
    const east = rect.east;
    const lng = carto.longitude;
    const inLon = lng >= west && lng <= east;
    return inLon && carto.latitude >= rect.south && carto.latitude <= rect.north;
  };

  const getEntityPositions = (entity) => {
    const t = timeNow();
    const result = [];

    // point / position
    const posProp = entity?.position;
    if (posProp && typeof posProp.getValue === "function") {
      const c = posProp.getValue(t);
      if (c) result.push(c);
    }

    // polygon
    const ph = entity?.polygon?.hierarchy;
    if (ph && typeof ph.getValue === "function") {
      try {
        const hierarchy = ph.getValue(t);
        const positions = hierarchy?.positions || hierarchy;
        if (Array.isArray(positions)) {
          for (const p of positions) {
            if (p) result.push(p);
          }
        }
      } catch {}
    }

    // polyline
    const pl = entity?.polyline?.positions;
    if (pl && typeof pl.getValue === "function") {
      try {
        const positions = pl.getValue(t);
        if (Array.isArray(positions)) {
          for (const p of positions) {
            if (p) result.push(p);
          }
        }
      } catch {}
    }

    return result;
  };

  const rectanglesOverlap = (r1, r2) => {
    if (!r1 || !r2) return false;
    const lonOverlap = !(r1.east < r2.west || r2.east < r1.west);
    const latOverlap = !(r1.north < r2.south || r2.north < r1.south);
    return lonOverlap && latOverlap;
  };

  const isEntityVisible = (entity) => {
    if (!entity) return false;
    if (entity.show === false) return false;
    const owner = entity?.entityCollection?.owner;
    if (owner && owner.show === false) return false;
    return true;
  };
  const getEntitiesInRectangle = (rect) => {
    const all = getAllEntities();
    const hits = [];

    for (const e of all) {
      if (!isEntityVisible(e)) continue;

      const rectProp = e?.rectangle?.coordinates;
      if (rectProp && typeof rectProp.getValue === "function") {
        try {
          const er = rectProp.getValue(timeNow());
          if (er && rectanglesOverlap(rect, er)) {
            hits.push(e);
            continue;
          }
        } catch {}
      }

      const positions = getEntityPositions(e);
      if (positions.length === 0) continue;

      const t = timeNow();
      const cartos = [];
      let inside = false;

      for (const p of positions) {
        const c = Ellipsoid.WGS84.cartesianToCartographic(p);
        if (!c) continue;
        cartos.push(c);
        if (cartographicInsideRectangle(rect, c)) {
          inside = true;
          break;
        }
      }

      if (!inside && cartos.length >= 2) {
        for (let i = 0; i < cartos.length - 1; i++) {
          if (segmentIntersectsRectangleCartographic(rect, cartos[i], cartos[i + 1])) {
            inside = true;
            break;
          }
        }
      }

      if (inside) hits.push(e);
    }

    return hits;
  };

  // --- Rectangles state ---
  const rectangles = []; // list of Rectangle
  const drawnRectEntities = []; // list of Cesium entities for boxes
  let liveRectEntity = null;

  const ensureLiveRectangle = () => {
    if (liveRectEntity) return;
    liveRectEntity = viewer.entities.add({
      rectangle: {
        coordinates: new CallbackProperty(() => currentRect, false),
        material: Color.YELLOW.withAlpha(0.15),
        outline: true,
        outlineColor: Color.YELLOW,
      },
    });
  };

  const removeLiveRectangle = () => {
    if (!liveRectEntity) return;
    viewer.entities.remove(liveRectEntity);
    liveRectEntity = null;
  };

  const clearDrawnRectangles = () => {
    for (const ent of drawnRectEntities) {
      try {
        viewer.entities.remove(ent);
      } catch {}
    }
    drawnRectEntities.length = 0;
    rectangles.length = 0;
    removeLiveRectangle();

    for (const id of boxSelectedIds) {
      if (lockedHighlightIds.has(id)) continue;
      const ent = findEntityById(id);
      if (ent) restoreEntity(ent);
    }
    boxSelectedIds.clear();
  };

  const history = [];

  // --- Parent-child rules ---
  // --- Parent-child rules ---
  const FAST_PAIRS = {
    lanelet: ["stopline", "yieldline"],
    stopline: ["trafficsignbox", "trafficlightpole"],
    yieldline: ["trafficsignbox", "trafficlightpole"],
    trafficlightpole: ["trafficlightbox"],
    trafficlightbox: ["trafficlightbulb"],
    trafficsignpole: ["trafficsignbox"],
  };

  const PROP_KEY_BY_PARENT = {
    lanelet: "lane_id",
    stopline: "stop_line_id",
    yieldline: "yield_line_id",
    trafficlightpole: "traffic_light_pole_id",
    trafficlightbox: "traffic_light_box_id",
    trafficsignpole: "traffic_sign_pole_id",
  };

  const VALID_LAYER_KEYS = new Set(["lanelet", "stopline", "yieldline", "trafficsignbox", "trafficlightpole", "trafficlightbox", "trafficlightbulb", "trafficsignpole"]);

  const setCesiumPropertyString = (entity, key, value) => {
    // set single value
    if (!entity.properties) entity.properties = new PropertyBag({});
    if (!(key in entity.properties)) {
      entity.properties.addProperty?.(key);
    }
    entity.properties[key] = new ConstantProperty(String(value));

    if (attEditor) {
      if (!("Att_editor" in entity.properties) || typeof entity.properties.Att_editor?.getValue !== "function") {
        entity.properties.addProperty?.("Att_editor");
      }
      entity.properties.Att_editor = new ConstantProperty(attEditor);
    }
  };

  const doFastAssociation = () => {
    if (rectangles.length === 0) {
      showToast("No selection boxes.", Type.NOTIFICATION.INFO, { closable: true });
      return;
    }

    const selectedMap = new Map();
    for (const rect of rectangles) {
      const ents = getEntitiesInRectangle(rect);
      for (const e of ents) {
        if (!e || !defined(e.id)) continue;
        if (!selectedMap.has(e.id)) selectedMap.set(e.id, e);
      }
    }

    if (selectedMap.size === 0) {
      showToast("No entities in boxes.", Type.NOTIFICATION.WARNING, { closable: true });
      return;
    }

    const groups = {
      lanelet: [],
      stopline: [],
      yieldline: [],
      trafficsignbox: [],
      trafficlightpole: [],
      trafficlightbox: [],
      trafficlightbulb: [],
      trafficsignpole: [],
    };

    for (const e of selectedMap.values()) {
      const key = getNormalizedLayerKey(e);
      if (groups[key]) groups[key].push(e);
    }

    let totalParents = 0;
    let totalChildren = 0;

    Object.entries(FAST_PAIRS).forEach(([parentKey, childTypes]) => {
      const parents = groups[parentKey] || [];
      if (parents.length === 0) return;

      const propKey = PROP_KEY_BY_PARENT[parentKey];
      if (!propKey) return;

      const parentIds = [];
      for (const p of parents) {
        const pid = getPropValue(p, "id");
        if (pid != null && String(pid) !== "") parentIds.push(String(pid));
      }
      if (parentIds.length === 0) return;

      if (parentKey !== "lanelet" && parentIds.length > 1) {
        showToast(`Selection error: ${parentKey} is single-parent. ${parentIds.length} parents were selected. Select only one ${parentKey} (or draw separate boxes).`, Type.NOTIFICATION.ERROR, {
          closable: true,
          duration: 10000,
        });
        return;
      }

      const parentEntityIds = parents.map((p) => p.id);
      totalParents += parents.length;

      for (const p of parents) {
        highlightEntity(p, Color.RED.withAlpha(0.75));
        lockedHighlightIds.add(p.id);
        boxSelectedIds.delete(p.id);
        markEdited(p);
      }

      for (const childType of childTypes) {
        const children = groups[childType] || [];
        if (children.length === 0) continue;

        for (const ch of children) {
          if (!ch || !defined(ch.id)) continue;

          if (parentKey === "lanelet") {
            for (const pid of parentIds) {
              upsertCesiumPropertyString(ch, propKey, pid);
            }

            history.push({
              childId: ch.id,
              propKey,
              parentIds: parentIds.map(String),
              parentEntityIds,
            });

            onAssociation?.({
              parentLayer: parentKey,
              parentId: parentIds.join(","),
              childLayer: childType,
              childEntityId: ch.id,
              propKey,
            });
          } else {
            const pid = String(parentIds[0]);
            setCesiumPropertyString(ch, propKey, pid);

            history.push({
              childId: ch.id,
              propKey,
              parentIds: [pid],
              parentEntityIds,
            });

            onAssociation?.({
              parentLayer: parentKey,
              parentId: pid,
              childLayer: childType,
              childEntityId: ch.id,
              propKey,
            });
          }

          highlightEntity(ch, Color.BLUE.withAlpha(0.7));
          lockedHighlightIds.add(ch.id);
          boxSelectedIds.delete(ch.id);
          markEdited(ch);
          totalChildren += 1;
        }
      }
    });

    if (totalParents === 0 || totalChildren === 0) {
      showToast("No valid parent-child pairs.", Type.NOTIFICATION.INFO, { closable: true });
    } else {
      showToast(`Fast associated ${totalChildren} child(ren) with ${totalParents} parent(s).`, Type.NOTIFICATION.SUCCESS, { closable: true });
    }
  };

  // --- Camera lock for drag ---
  const originalCameraControls = { current: null };

  // --- Mouse handler ---
  const handler = new ScreenSpaceEventHandler(viewer?.scene?.canvas);

  let dragStartPos = null;
  let isDragging = false;

  const onWindowMouseUp = () => {
    if (!dragStartPos) return;

    dragStartPos = null;
    isDragging = false;

    restoreCameraControls?.(viewer, originalCameraControls);
    removeLiveRectangle();

    if (viewer?.scene?.requestRenderMode) viewer.scene.requestRender();
  };

  if (typeof window !== "undefined") {
    window.addEventListener("mouseup", onWindowMouseUp);
  }

  // LEFT_DOWN → start box
  handler.setInputAction((movement) => {
    const isDis = disableCameraControls?.(viewer, originalCameraControls);
    if (!isDis) return;
    dragStartPos = movement?.position || movement;
    isDragging = false;
  }, ScreenSpaceEventType.LEFT_DOWN);

  // MOUSE_MOVE → live box
  handler.setInputAction((movement) => {
    if (!dragStartPos) return;
    const cur = movement?.endPosition || movement;
    if (!isDragging && Math.hypot(cur.x - dragStartPos.x, cur.y - dragStartPos.y) > 5) isDragging = true;
    if (!isDragging) return;
    ensureLiveRectangle();
    if (updateCurrentRectFromWindow(dragStartPos, cur) && viewer.scene.requestRenderMode) {
      viewer.scene.requestRender();
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);

  // LEFT_UP → finalize box, store rect
  handler.setInputAction((movement) => {
    const wasDragging = isDragging;
    const endPos = movement?.position || movement;

    let finalRect = null;
    if (wasDragging && updateCurrentRectFromWindow(dragStartPos, endPos)) {
      finalRect = Rectangle.fromRadians(currentRect.west, currentRect.south, currentRect.east, currentRect.north);
    }

    dragStartPos = null;
    isDragging = false;

    restoreCameraControls?.(viewer, originalCameraControls);
    removeLiveRectangle();

    if (wasDragging && finalRect) {
      const rectEnt = viewer.entities.add({
        rectangle: {
          coordinates: finalRect,
          material: Color.ORANGE.withAlpha(0.12),
          outline: true,
          outlineColor: Color.ORANGE,
        },
      });

      const allEnts = getEntitiesInRectangle(finalRect);

      const ents = allEnts.filter((e) => {
        if (!e || !defined(e.id)) return false;
        const key = getNormalizedLayerKey(e);
        return VALID_LAYER_KEYS.has(key);
      });

      if (!ents || ents.length === 0) {
        try {
          viewer.entities.remove(rectEnt);
        } catch {}
        if (viewer.scene.requestRenderMode) viewer.scene.requestRender();
        showToast("No entities in area.", Type.NOTIFICATION.WARNING, { closable: true });
        return;
      }

      rectangles.push(finalRect);
      drawnRectEntities.push(rectEnt);

      for (const e of ents) {
        if (!e || !defined(e.id)) continue;
        if (lockedHighlightIds.has(e.id)) continue;
        if (boxSelectedIds.has(e.id)) continue;
        highlightEntity(e, Color.ORANGE.withAlpha(0.7));
        boxSelectedIds.add(e.id);
      }

      if (viewer.scene.requestRenderMode) viewer.scene.requestRender();
    }
  }, ScreenSpaceEventType.LEFT_UP);

  // RIGHT_CLICK → apply associations, then clear boxes
  handler.setInputAction(() => {
    doFastAssociation();
    clearDrawnRectangles();
    if (viewer.scene.requestRenderMode) viewer.scene.requestRender();
  }, ScreenSpaceEventType.RIGHT_CLICK);

  showToast("Fast mode: draw boxes with left mouse. Right click to associate.", Type.NOTIFICATION.INFO, { closable: true });

  const undoLast = () => {
    if (history.length === 0) {
      showToast("Nothing to undo.", Type.NOTIFICATION.INFO, { closable: true });
      return;
    }
    const last = history.pop();

    const child = findEntityById(last.childId);
    if (child) {
      if (last.propKey && Array.isArray(last.parentIds)) {
        for (const pid of last.parentIds) {
          removeCesiumPropertyString(child, last.propKey, pid);
        }
      }
      restoreEntity(child);
      lockedHighlightIds.delete(child.id);
      boxSelectedIds.delete(child.id);
    }

    if (Array.isArray(last.parentEntityIds)) {
      for (const pid of last.parentEntityIds) {
        const parent = findEntityById(pid);
        if (!parent) continue;
        restoreEntity(parent);
        lockedHighlightIds.delete(parent.id);
      }
    }

    if (viewer.scene.requestRenderMode) viewer.scene.requestRender();

    showToast("Last association undone.", Type.NOTIFICATION.SUCCESS, { closable: true });
  };

  return {
    destroy: () => {
      try {
        handler?.destroy();
      } finally {
        clearDrawnRectangles();
        if (originalCameraControls.current) restoreCameraControls?.(viewer, originalCameraControls);
        for (const id of lockedHighlightIds) restoreEntity(findEntityById(id));
        lockedHighlightIds.clear();
        restoreAll();
        dragStartPos = null;
        isDragging = false;
        history.length = 0;
        if (typeof window !== "undefined") {
          window.removeEventListener("mouseup", onWindowMouseUp);
        }
      }
    },
    undoLast,
  };
};
